import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { GA4EventName, GA4FeatureCategory, type GA4ItemListName } from '@jsmdg/tracking';
import { ChevronIcon, Link, LinkVariant, type SliderSlide, SlidingDirections } from '@jsmdg/yoshi';
import { type PageType } from '../../../shared/enums/pageType';
import { type Product } from '../../../shared/types/searchResponse';
import { CreativeName } from '../../enums/trackingPromotions';
import { handleSlidingDirections } from '../../helper/handleSlidingDirections.tracking';
import { Headline } from '../Headline';
import { LazySlider } from '../LazySlider';
import { ProductTileWrapper } from '../ProductTileWrapper';
import { productSliderConfig } from './productSliderConfig';
import styles from './ProductSlider.module.scss';

type ProductSliderProps = {
    readonly products: Product[];
    readonly title?: string;
    readonly urlTitle?: string;
    readonly url?: string;
    readonly trackingCategory?: string;
    readonly trackingListName?: GA4ItemListName;
    readonly dataTestId?: string;
    readonly lazyLoad: boolean;
    readonly pageType: PageType;
    readonly verticalPosition?: number;
};

const messages = defineMessages({
    a11yPreviousSlide: { defaultMessage: 'Zur vorherigen Seite' },
    a11yNextSlide: { defaultMessage: 'Zur nächsten Seite' },
});

const ProductSlider = ({
    dataTestId,
    lazyLoad,
    pageType,
    products,
    title,
    trackingCategory,
    trackingListName,
    url,
    urlTitle,
    verticalPosition,
}: ProductSliderProps): JSX.Element | null => {
    const intl = useIntl();
    const sliderConfig = productSliderConfig[pageType];

    if (!products.length || !sliderConfig) {
        return null;
    }

    const slides: SliderSlide[] = products.map((product, index) => ({
        key: product.productId,
        content: (
            <div className={classNames(styles.tile, 'w-100 p-0-5x')}>
                <ProductTileWrapper
                    product={product}
                    position={index + 1}
                    trackingListName={trackingListName}
                    lazyLoad={lazyLoad}
                />
            </div>
        ),
    }));

    const basicEventData = {
        eventName: GA4EventName.SlidePromotionItem,
        feature_category: GA4FeatureCategory.PromotionStatic,
        promotion_type: CreativeName.ProductTile,
        position_vertical: verticalPosition?.toString(),
    };

    const handleSlide = async (direction: SlidingDirections): Promise<void> =>
        handleSlidingDirections({
            direction,
            trackingCategory,
            eventData: {
                ...basicEventData,
                click_type: `Slide ${direction}`,
            },
        });

    return (
        <div data-testid={dataTestId}>
            <div className="d-flex justify-content-between flex-wrap mb-2x w-100 align-items-baseline">
                {title && <Headline title={title} />}
                {urlTitle ? (
                    <Link
                        className={`${styles.showMore} d-inline-flex align-content-center`}
                        href={url}
                        variant={LinkVariant.Brand}
                        internal
                    >
                        <span>{urlTitle}</span>
                        <ChevronIcon />
                    </Link>
                ) : null}
            </div>
            <LazySlider
                a11yNextSlide={intl.formatMessage(messages.a11yNextSlide)}
                a11yPreviousSlide={intl.formatMessage(messages.a11yPreviousSlide)}
                slides={slides}
                config={sliderConfig}
                handleShowNext={async () => handleSlide(SlidingDirections.Next)}
                handleShowPrevious={async () => handleSlide(SlidingDirections.Previous)}
                classes={{
                    swiperWrapper: styles.shadowRoom,
                    swiperSlide: styles.swiperSlide,
                    controlsContainer: classNames('mb-0', styles.controlsContainer),
                }}
                freeScrollOnMobile
            />
        </div>
    );
};

export { ProductSlider };

export default ProductSlider;
